import { useState, useEffect } from "react";
import { 
    ArrowRight, 
    ArrowLeft,
    CheckCircle, 
    XCircle,
    AlertTriangle,
    TrendingUp,
    Download,
    Calendar,
    Loader2,
    DollarSign,
    ShoppingCart,
    Mail,
    Star,
    MousePointerClick,
    Smartphone,
    Zap,
    Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import ServicesPromo from "./ServicesPromo";
import LeadCaptureGate from "./LeadCaptureGate";
import { downloadProfitLeakFixPlan } from "./utils/generateProfitLeakPDF";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Questions for the calculator
const QUESTIONS = [
    {
        id: "email_capture",
        question: "Do you have an email capture form?",
        icon: Mail,
        description: "Pop-ups, embedded forms, or lead magnets to collect visitor emails"
    },
    {
        id: "abandoned_cart",
        question: "Do you have abandoned cart emails enabled?",
        icon: ShoppingCart,
        description: "Automated emails sent to users who leave items in their cart"
    },
    {
        id: "upsells_bundles",
        question: "Do you have upsells or bundles?",
        icon: DollarSign,
        description: "Product recommendations, add-ons, or bundle deals at checkout"
    },
    {
        id: "customer_reviews",
        question: "Do you have customer reviews displayed?",
        icon: Star,
        description: "Visible testimonials or star ratings on product/service pages"
    },
    {
        id: "cta_above_fold",
        question: "Do you have a clear call-to-action above the fold?",
        icon: MousePointerClick,
        description: "Prominent button or action visible without scrolling"
    },
    {
        id: "mobile_optimized",
        question: "Is your site mobile optimized?",
        icon: Smartphone,
        description: "Responsive design that works well on phones and tablets"
    },
    {
        id: "fast_loading",
        question: "Do you load in under 3 seconds?",
        icon: Zap,
        description: "Quick page load times for better user experience"
    }
];

const POINTS_PER_QUESTION = 10;
const MAX_SCORE = QUESTIONS.length * POINTS_PER_QUESTION;

// Get feedback based on score
const getFeedback = (score) => {
    if (score <= 20) {
        return {
            title: "Critical Revenue Leaks Detected",
            color: "text-red-500",
            bgColor: "bg-red-500/10",
            borderColor: "border-red-500/30",
            icon: AlertTriangle,
            description: "Your website has significant gaps that are likely costing you substantial revenue. Immediate action is recommended."
        };
    } else if (score <= 50) {
        return {
            title: "Moderate Revenue Gaps",
            color: "text-amber-500",
            bgColor: "bg-amber-500/10",
            borderColor: "border-amber-500/30",
            icon: TrendingUp,
            description: "You have some optimization in place, but there are clear opportunities to capture more revenue with targeted improvements."
        };
    } else {
        return {
            title: "Strong Optimization — Minor Improvements Available",
            color: "text-emerald-500",
            bgColor: "bg-emerald-500/10",
            borderColor: "border-emerald-500/30",
            icon: CheckCircle,
            description: "Your site is well-optimized. A few tweaks could help you maximize your conversion potential even further."
        };
    }
};

export default function ProfitLeakCalculator({ onBack, user, subscription }) {
    const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-7 = questions, 8 = results
    const [answers, setAnswers] = useState({});
    const [isCalculating, setIsCalculating] = useState(false);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [isPDFDownloaded, setIsPDFDownloaded] = useState(false);

    // Check if user has Pro/Agency access (paid tier or test user)
    const isPaidUser = subscription?.tier === 'pro' || subscription?.tier === 'agency' || subscription?.tier === 'premium' ||
                       user?.subscription_tier === 'pro' || user?.subscription_tier === 'agency' || user?.subscription_tier === 'premium';
    const isTestUser = user?.email === 'testpro@sitegradepro.com' || user?.user_id === 'test_pro_user_123';

    // Check for payment success on mount and restore state
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const paymentSuccess = urlParams.get('payment_success');
        const planId = urlParams.get('plan_id');
        
        if (paymentSuccess === 'true' && planId === 'profit_fix_plan') {
            // Restore saved state from sessionStorage
            const savedState = sessionStorage.getItem('profitLeakState');
            if (savedState) {
                const { answers: savedAnswers, leadData: savedLead, score: savedScore } = JSON.parse(savedState);
                setAnswers(savedAnswers);
                setLeadData(savedLead);
                setIsUnlocked(true);
                setCurrentStep(QUESTIONS.length + 1);
                
                // Generate and download PDF
                setTimeout(async () => {
                    try {
                        await downloadProfitLeakFixPlan({
                            businessName: savedLead?.name || "Your Business",
                            websiteUrl: savedLead?.website || "yourwebsite.com",
                            answers: savedAnswers,
                            score: savedScore,
                            maxScore: MAX_SCORE,
                            leadData: savedLead
                        });
                        toast.success("Your Profit Leak Fix Plan has been downloaded!");
                        setIsPDFDownloaded(true);
                        sessionStorage.removeItem('profitLeakState');
                    } catch (error) {
                        console.error("PDF generation failed:", error);
                        toast.error("Failed to generate PDF. Please contact support.");
                    }
                }, 1000);
            }
            
            // Clean URL
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('payment_success');
            newUrl.searchParams.delete('plan_id');
            window.history.replaceState({}, '', newUrl.toString());
        }
    }, []);

    const handleAnswer = (questionId, answer) => {
        setAnswers(prev => ({ ...prev, [questionId]: answer }));
        
        // Auto-advance after short delay
        setTimeout(() => {
            if (currentStep < QUESTIONS.length) {
                setCurrentStep(currentStep + 1);
            } else {
                // Show results
                setIsCalculating(true);
                setTimeout(() => {
                    setIsCalculating(false);
                    setCurrentStep(QUESTIONS.length + 1);
                }, 1500);
            }
        }, 300);
    };

    const calculateScore = () => {
        return Object.values(answers).filter(a => a === true).length * POINTS_PER_QUESTION;
    };

    const handleDownloadFixPlan = async () => {
        // Pro/Agency users or test users get free PDF download
        if (isPaidUser || isTestUser) {
            handleDirectDownload();
            toast.success("PDF downloaded! (Pro benefit)");
            return;
        }

        // Free users go through Stripe checkout
        setIsCheckingOut(true);
        try {
            // Save current state to sessionStorage before redirect
            sessionStorage.setItem('profitLeakState', JSON.stringify({
                answers,
                leadData,
                score: calculateScore()
            }));
            
            const response = await axios.post(`${API}/checkout`, {
                plan_id: "profit_fix_plan",
                origin_url: window.location.origin,
                email: leadData?.email || null,
                success_url_params: `tool=profit-calculator&payment_success=true&plan_id=profit_fix_plan`
            });
            window.location.href = response.data.url;
        } catch (error) {
            console.error("Checkout failed:", error);
            toast.error("Failed to start checkout. Please try again.");
            setIsCheckingOut(false);
        }
    };

    // Direct PDF download (for already purchased or demo)
    const handleDirectDownload = async () => {
        try {
            await downloadProfitLeakFixPlan({
                businessName: leadData?.name || "Your Business",
                websiteUrl: leadData?.website || "yourwebsite.com",
                answers,
                score: calculateScore(),
                maxScore: MAX_SCORE,
                leadData
            });
            toast.success("Your Profit Leak Fix Plan has been downloaded!");
        } catch (error) {
            console.error("PDF generation failed:", error);
            toast.error("Failed to generate PDF.");
        }
    };

    const handleBookOptimization = () => {
        // Scroll to consultation or open modal
        if (onBack) {
            onBack("consultation");
        }
    };

    const resetCalculator = () => {
        setCurrentStep(0);
        setAnswers({});
    };

    const score = calculateScore();
    const feedback = getFeedback(score);
    const progress = currentStep === 0 ? 0 : ((currentStep) / (QUESTIONS.length + 1)) * 100;

    return (
        <div className="min-h-screen bg-[#0a0a0a]">
            {/* Subtle grid background */}
            <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />
            
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <button 
                        onClick={() => onBack && onBack()}
                        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                        data-testid="back-to-main-btn"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="text-sm">Back to SiteGradePro</span>
                    </button>
                    {currentStep > 0 && currentStep <= QUESTIONS.length && (
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">
                                {currentStep} of {QUESTIONS.length}
                            </span>
                            <div className="w-32 h-1 bg-white/10 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-300"
                                    style={{ width: `${(currentStep / QUESTIONS.length) * 100}%` }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
                <div className="w-full max-w-2xl">
                    
                    {/* Intro Screen */}
                    {currentStep === 0 && (
                        <div className="text-center animate-fade-in" data-testid="calculator-intro">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm mb-8">
                                <DollarSign className="h-4 w-4" />
                                Free Revenue Assessment
                            </div>
                            
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                                Profit Leak Calculator
                            </h1>
                            
                            <p className="text-lg text-gray-400 mb-10 max-w-lg mx-auto leading-relaxed">
                                Answer 7 quick questions to discover where your website 
                                might be losing revenue — and how to fix it.
                            </p>
                            
                            <Button
                                onClick={() => setCurrentStep(1)}
                                size="lg"
                                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold px-10 py-6 text-lg rounded-lg shadow-lg shadow-cyan-500/20"
                                data-testid="start-calculator-btn"
                            >
                                Start Calculator
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                            
                            <p className="text-sm text-gray-500 mt-6">
                                Takes less than 2 minutes • No email required
                            </p>
                        </div>
                    )}

                    {/* Question Screens */}
                    {currentStep >= 1 && currentStep <= QUESTIONS.length && (
                        <div className="animate-fade-in" data-testid={`question-${currentStep}`}>
                            {(() => {
                                const question = QUESTIONS[currentStep - 1];
                                const Icon = question.icon;
                                const isAnswered = answers[question.id] !== undefined;
                                
                                return (
                                    <Card className="bg-[#111111] border-white/10 overflow-hidden">
                                        <CardContent className="p-8 md:p-12">
                                            {/* Question Icon */}
                                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center mb-8">
                                                <Icon className="h-8 w-8 text-cyan-400" />
                                            </div>
                                            
                                            {/* Question Text */}
                                            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                                                {question.question}
                                            </h2>
                                            <p className="text-gray-400 mb-10">
                                                {question.description}
                                            </p>
                                            
                                            {/* Answer Buttons */}
                                            <div className="flex flex-col sm:flex-row gap-4">
                                                <Button
                                                    onClick={() => handleAnswer(question.id, true)}
                                                    variant="outline"
                                                    size="lg"
                                                    className={`flex-1 py-6 text-lg font-medium transition-all ${
                                                        answers[question.id] === true
                                                            ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                                                            : 'border-white/20 text-white hover:bg-white/5 hover:border-white/40'
                                                    }`}
                                                    data-testid="answer-yes-btn"
                                                >
                                                    <CheckCircle className="mr-2 h-5 w-5" />
                                                    Yes
                                                </Button>
                                                <Button
                                                    onClick={() => handleAnswer(question.id, false)}
                                                    variant="outline"
                                                    size="lg"
                                                    className={`flex-1 py-6 text-lg font-medium transition-all ${
                                                        answers[question.id] === false
                                                            ? 'bg-red-500/20 border-red-500 text-red-400'
                                                            : 'border-white/20 text-white hover:bg-white/5 hover:border-white/40'
                                                    }`}
                                                    data-testid="answer-no-btn"
                                                >
                                                    <XCircle className="mr-2 h-5 w-5" />
                                                    No
                                                </Button>
                                            </div>
                                            
                                            {/* Navigation */}
                                            {currentStep > 1 && (
                                                <button
                                                    onClick={() => setCurrentStep(currentStep - 1)}
                                                    className="mt-8 text-sm text-gray-500 hover:text-white transition-colors flex items-center gap-1"
                                                >
                                                    <ArrowLeft className="h-3 w-3" />
                                                    Previous question
                                                </button>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })()}
                        </div>
                    )}

                    {/* Calculating Animation */}
                    {isCalculating && (
                        <div className="text-center animate-fade-in" data-testid="calculating-state">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/20 flex items-center justify-center mx-auto mb-8">
                                <Loader2 className="h-10 w-10 text-cyan-400 animate-spin" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-3">
                                Analyzing Your Responses...
                            </h2>
                            <p className="text-gray-400">
                                Calculating potential revenue impact
                            </p>
                        </div>
                    )}

                    {/* Results Screen */}
                    {currentStep === QUESTIONS.length + 1 && !isCalculating && (
                        <div className="animate-fade-in" data-testid="calculator-results">
                            {/* Lead Capture Gate - Show before full results */}
                            {!isUnlocked ? (
                                <LeadCaptureGate
                                    score={score}
                                    maxScore={MAX_SCORE}
                                    toolName="Profit Leak Calculator"
                                    previewContent={
                                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${feedback.bgColor} ${feedback.color}`}>
                                            <feedback.icon className="h-5 w-5" />
                                            <span className="font-semibold">{feedback.title}</span>
                                        </div>
                                    }
                                    onUnlock={(data) => {
                                        setLeadData(data);
                                        setIsUnlocked(true);
                                    }}
                                />
                            ) : (
                            <>
                            {/* Score Display */}
                            <Card className={`bg-[#111111] border ${feedback.borderColor} overflow-hidden mb-6`}>
                                <CardContent className="p-8 md:p-12 text-center">
                                    {/* Score Circle */}
                                    <div className="relative w-40 h-40 mx-auto mb-8">
                                        <svg className="w-full h-full transform -rotate-90">
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                stroke="rgba(255,255,255,0.1)"
                                                strokeWidth="8"
                                                fill="none"
                                            />
                                            <circle
                                                cx="80"
                                                cy="80"
                                                r="70"
                                                stroke="url(#scoreGradient)"
                                                strokeWidth="8"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeDasharray={`${(score / MAX_SCORE) * 440} 440`}
                                                className="transition-all duration-1000"
                                            />
                                            <defs>
                                                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#06b6d4" />
                                                    <stop offset="100%" stopColor="#3b82f6" />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                                            <span className="text-5xl font-bold text-white">{score}</span>
                                            <span className="text-gray-500 text-sm">out of {MAX_SCORE}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Feedback */}
                                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${feedback.bgColor} ${feedback.color} mb-4`}>
                                        <feedback.icon className="h-5 w-5" />
                                        <span className="font-semibold">{feedback.title}</span>
                                    </div>
                                    
                                    <p className="text-gray-400 max-w-md mx-auto">
                                        {feedback.description}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Breakdown */}
                            <Card className="bg-[#111111] border-white/10 mb-8">
                                <CardContent className="p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Your Results Breakdown</h3>
                                    <div className="space-y-3">
                                        {QUESTIONS.map((q, index) => {
                                            const isYes = answers[q.id] === true;
                                            const Icon = q.icon;
                                            return (
                                                <div 
                                                    key={q.id}
                                                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Icon className="h-4 w-4 text-gray-500" />
                                                        <span className="text-gray-300 text-sm">{q.question}</span>
                                                    </div>
                                                    {isYes ? (
                                                        <span className="flex items-center gap-1 text-emerald-400 text-sm">
                                                            <CheckCircle className="h-4 w-4" />
                                                            +10
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-red-400 text-sm">
                                                            <XCircle className="h-4 w-4" />
                                                            0
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* CTAs */}
                            <div className="space-y-4">
                                {/* Pro user badge */}
                                {(isPaidUser || isTestUser) && (
                                    <div className="flex items-center justify-center gap-2 text-violet-400 text-sm mb-2">
                                        <Crown className="h-4 w-4" />
                                        <span>Pro Benefit: Free PDF Download</span>
                                    </div>
                                )}
                                
                                {isPDFDownloaded ? (
                                    <>
                                        {/* Already downloaded - show download again option */}
                                        <div className="text-center p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg mb-4">
                                            <CheckCircle className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                                            <p className="text-emerald-400 font-semibold">
                                                {(isPaidUser || isTestUser) ? "PDF Downloaded!" : "Payment Successful!"}
                                            </p>
                                            <p className="text-gray-400 text-sm">Your Fix Plan has been downloaded.</p>
                                        </div>
                                        <Button
                                            onClick={handleDirectDownload}
                                            size="lg"
                                            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-cyan-500/20"
                                            data-testid="redownload-fix-plan-btn"
                                        >
                                            <Download className="mr-2 h-5 w-5" />
                                            Download Again
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        onClick={handleDownloadFixPlan}
                                        disabled={isCheckingOut}
                                        size="lg"
                                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold py-6 text-lg rounded-lg shadow-lg shadow-cyan-500/20"
                                        data-testid="download-fix-plan-btn"
                                    >
                                        {isCheckingOut ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (isPaidUser || isTestUser) ? (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Download Full Fix Plan
                                            </>
                                        ) : (
                                            <>
                                                <Download className="mr-2 h-5 w-5" />
                                                Download Full Fix Plan — $19
                                            </>
                                        )}
                                    </Button>
                                )}
                                
                                <Button
                                    onClick={handleBookOptimization}
                                    variant="outline"
                                    size="lg"
                                    className="w-full border-white/20 text-white hover:bg-white/5 hover:border-white/40 py-6 text-lg rounded-lg"
                                    data-testid="book-optimization-btn"
                                >
                                    <Calendar className="mr-2 h-5 w-5" />
                                    Book Done-For-You Optimization
                                </Button>
                            </div>

                            {/* Reset */}
                            <button
                                onClick={resetCalculator}
                                className="w-full mt-6 text-sm text-gray-500 hover:text-white transition-colors"
                                data-testid="retake-quiz-btn"
                            >
                                Retake Assessment
                            </button>

                            {/* Services Promo */}
                            <ServicesPromo onBookService={(serviceId) => {
                                if (onBack) onBack("consultation");
                            }} />
                            </>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
